import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { useAuth } from '../../auth/AuthContext';
import { FAQS } from './faqContent';
import { renderRich, userRoles } from './Faq';
import { GUIDES, type Guide } from './gettingStartedContent';

const FAQ_BY_ID = new Map(FAQS.map((f) => [f.id, f]));
const ROLE_ORDER = ['superadmin', 'tenant_admin', 'coordinator', 'participant'];

function GuideView({ guide }: { guide: Guide }) {
  return (
    <Stack spacing={2}>
      <Typography variant="body1">{guide.intro}</Typography>
      <Stack component="ol" spacing={2} sx={{ pl: 2.5, m: 0 }}>
        {guide.steps.map((step) => (
          <Stack component="li" key={step.title} spacing={0.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              {step.title}
            </Typography>
            <Typography variant="body2">{renderRich(step.body)}</Typography>
            {step.faq && step.faq.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Learn more:{' '}
                {step.faq
                  .map((id) => FAQ_BY_ID.get(id))
                  .filter((f) => f !== undefined)
                  .map((f, i) => (
                    <span key={f.id}>
                      {i > 0 && ' · '}
                      <Link component={RouterLink} to={`/help/faq#faq-${f.id}`}>
                        {f.q}
                      </Link>
                    </span>
                  ))}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

/**
 * Getting Started — a short path for each role the user holds. Someone with
 * more than one role (an admin who is also a participant) gets a tab per
 * role instead of only the "most privileged" one; coordinators have their
 * own path.
 */
export function GettingStarted() {
  const { user } = useAuth();
  const roles = useMemo(() => userRoles(user), [user]);
  // Most responsibility first: an admin who is also a participant opens on
  // the admin path, with their participant path one tab away.
  const guides = useMemo(
    () =>
      GUIDES.filter((g) => roles.has(g.role)).sort(
        (a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role),
      ),
    [roles],
  );
  const [tab, setTab] = useState(0);
  const guide = guides[Math.min(tab, guides.length - 1)];

  return (
    <Paper sx={{ p: 3, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
      <Stack spacing={2}>
        <Typography variant="h5">Getting started</Typography>
        {guides.length === 0 ? (
          <Alert severity="info">
            Your account doesn't hold an active role yet. Sign out and back in,
            or ask your organisation's admin if this is unexpected.
          </Alert>
        ) : (
          <>
            {guides.length > 1 && (
              <Tabs value={tab} onChange={(_e, v: number) => setTab(v)}>
                {guides.map((g) => (
                  <Tab key={g.role} label={g.title} />
                ))}
              </Tabs>
            )}
            {guides.length === 1 && (
              <Typography variant="h6">{guide.title}</Typography>
            )}
            <GuideView guide={guide} />
          </>
        )}
      </Stack>
    </Paper>
  );
}
